import type { CreateParent, SmartRecordCreatableType } from '../types';
import type ShapeSmartRecord from '../shapes/ShapeSmartRecord';
import type Shape from '../objects/Shape';
import type SmartRecordsController from '../controllers/SmartRecordsController';
import { pluralize, singularize } from 'inflected';
import { getConnectorAttachmentObjects } from '../utils/connectors';
import { CREATE_ORDER } from './SmartRecordConverter';

export interface FieldsValue {
  project?: CreateParent;
  release?: CreateParent;
  feature?: CreateParent;
}

export const recordTypeAsField = (recordType: SmartRecordCreatableType) => {
  return singularize(recordType);
};

export const fieldAsRecordType = (field: string): SmartRecordCreatableType => {
  return pluralize(field) as SmartRecordCreatableType;
};

// Manages the recordData for the SmartRecordsController. Provides an interface
// for accessing the fields and records for a given recordType.
class SmartRecordDataManager {
  fields: Record<SmartRecordCreatableType, FieldsValue>;

  records: Record<
    SmartRecordCreatableType,
    Record<RecordId, Shape<ShapeSmartRecord>>
  >;

  selections: Record<RecordId, boolean>;
  controller: SmartRecordsController;

  constructor(controller: SmartRecordsController) {
    this.controller = controller;
    this.initializeData();
  }

  get allRecords() {
    return this.availableRecordTypes.reduce(
      (
        acc: Shape<ShapeSmartRecord>[],
        type: SmartRecordCreatableType
      ): Shape<ShapeSmartRecord>[] => {
        acc.push(...this.recordsForTypeAsArray(type));
        return acc;
      },
      []
    );
  }

  get recordSelectedCount() {
    return Object.values(this.selections).filter(Boolean).length;
  }

  get readyToSubmit() {
    // Ensure at least one record is selected
    if (!this.isRecordSelected) return false;

    // Ensure that every required field for each recordType with selected records
    // is filled out
    return Object.entries(this.fields).every(
      ([recordType, fields]: [SmartRecordCreatableType, FieldsValue]) => {
        if (!this.recordSelectedForType(recordType)) return true;
        const requiredFields = this.requiredFieldsForRecordType(recordType);
        const recordTypeFields = Object.keys(requiredFields).every(
          field => Boolean(fields[field]) === requiredFields[field]
        );

        if (recordTypeFields) {
          return true;
        }

        const recordsForType = this.recordsForTypeAsArray(recordType);
        return recordsForType.every(record => {
          if (!this.recordSelected(record)) return true;
          const recordFields = this.getRecordFields(record);
          return Object.keys(requiredFields)
            .filter(field => requiredFields[field])
            .every(
              field => Boolean(recordFields[field]) === requiredFields[field]
            );
        });
      }
    );
  }

  // Record types for which there are smart records being converted
  get availableRecordTypes(): SmartRecordCreatableType[] {
    return Object.keys(this.records).filter(
      key => Object.keys(this.records[key]).length > 0
    ) as SmartRecordCreatableType[];
  }

  requiredFieldsForRecordType(recordType: SmartRecordCreatableType) {
    switch (recordType) {
      case 'initiatives':
      case 'ideas':
      case 'goals':
      case 'releases':
        return {
          project: true,
          release: false,
        };
      case 'epics':
      case 'features':
        return { project: false, release: true };
      case 'requirements':
        return { project: false, release: false, feature: true };
      default:
        return {};
    }
  }

  userSelectedFieldsForRecordType(recordType: SmartRecordCreatableType) {
    const requiredFields = this.requiredFieldsForRecordType(recordType);
    const records = this.recordsForTypeAsArray(recordType).filter(record =>
      this.recordSelected(record)
    );
    if (records.every(record => this.getParent(record) !== undefined)) {
      return {};
    }
    return requiredFields;
  }

  recordsForTypeAsArray(recordType: SmartRecordCreatableType) {
    return Object.values(this.records[recordType]);
  }

  recordCountForType(recordType: SmartRecordCreatableType) {
    return this.recordsForTypeAsArray(recordType).length;
  }

  recordSelected(shape: Shape<ShapeSmartRecord>) {
    return this.selections[shape.id];
  }

  recordSelectedForType(recordType: SmartRecordCreatableType) {
    return this.recordsForTypeAsArray(recordType).some(record =>
      this.recordSelected(record)
    );
  }

  resetRecords() {
    this.records = {
      goals: {},
      initiatives: {},
      releases: {},
      epics: {},
      features: {},
      requirements: {},
      ideas: {},
    };
  }

  resetFields() {
    this.initializeData();
  }

  resetSelections() {
    this.selections = {};
  }

  // When we refresh the list of records, we need to remove any entries in the
  // selections for records that may have been removed/converted
  private removeStaleSelections() {
    const ids = this.allRecords.map(record => record.id);
    for (const id of Object.keys(this.selections)) {
      if (!ids.includes(id)) {
        delete this.selections[id];
      }
    }
  }

  setRecords(shapes: Shape<ShapeSmartRecord>[], trySelectAll = false) {
    const shouldSelect = trySelectAll && !this.isRecordSelected;
    shapes.forEach(shape => {
      const { recordType } = shape;
      if (recordType) {
        this.records[recordType][shape.id] = shape;
        // Default to false without overwriting user selection
        this.selections[shape.id] ||= shouldSelect;
      }
    });

    this.removeStaleSelections();
  }

  setProject(recordType: SmartRecordCreatableType, project: CreateParent) {
    this.fields[recordType].project = project;
  }

  setRelease(recordType: SmartRecordCreatableType, release: CreateParent) {
    this.fields[recordType].release = release;
  }

  setFeature(recordType: SmartRecordCreatableType, parent: CreateParent) {
    this.fields[recordType].feature = parent;
  }

  setSelected(id: string, selected: boolean) {
    this.selections[id] = selected;
  }

  toggleSelectAll(selected: boolean) {
    this.availableRecordTypes.forEach(recordType => {
      this.recordsForTypeAsArray(recordType).forEach(record =>
        this.setSelected(record.id, selected)
      );
    });
  }

  // With the advent of connected shapes as parents, each smart shape can have
  // multiple parents. getHierarchy has some basic heuristics to choose the "best"
  // parents, so in this function we just need to choose the "highest order" type,
  // e.g., release over epic, if a feature has both as parents.
  getParent(
    shape: Shape<ShapeSmartRecord>
  ): Shape<ShapeSmartRecord> | undefined {
    const hierarchy = this.getHierarchy(shape);

    // CREATE_ORDER will give us the most senior record type at index 0
    const sortedHierarchy = Object.entries(hierarchy).sort(
      ([a], [b]) =>
        CREATE_ORDER.indexOf(fieldAsRecordType(a)) -
        CREATE_ORDER.indexOf(fieldAsRecordType(b))
    );

    const node = sortedHierarchy.find(([, value]) => {
      const record = this.findRecord(value.id);
      if (record) {
        return (
          this.recordSelected(record) &&
          this.isParentForChild(
            record.recordType as SmartRecordCreatableType,
            shape.recordType as SmartRecordCreatableType
          )
        );
      }
      return false;
    });

    if (node) {
      return this.findRecord(node[1].id);
    }
  }

  // Gets the fields for a record, prioritizing parent fields if available
  getRecordFields(shape: Shape<ShapeSmartRecord>): FieldsValue {
    const fields = this.getHierarchy(shape);
    this.useHierarchySelectedFields(fields);
    this.useSelectedFields(shape, fields);
    this.pruneFields(shape, fields);

    return fields;
  }

  private getHierarchy(shape: Shape<ShapeSmartRecord>): FieldsValue {
    const fields = {};
    this.constructHierarchy(shape, fields);
    return fields;
  }

  private initializeData() {
    this.fields = {
      initiatives: { project: undefined, release: undefined },
      releases: { project: undefined, release: undefined },
      ideas: { project: undefined, release: undefined },
      goals: { project: undefined, release: undefined },
      epics: { project: undefined, release: undefined },
      features: { project: undefined, release: undefined },
      requirements: {
        project: undefined,
        release: undefined,
        feature: undefined,
      },
    };

    this.selections = {};

    this.resetRecords();
  }

  findRecord(id: RecordId): Shape<ShapeSmartRecord> | undefined {
    const recordType = Object.keys(this.records).find(
      recordType => this.records[recordType][id]
    );

    if (recordType) {
      return this.records[recordType][id];
    }
  }

  // Inherit the selected fields from parents in the hierarchy
  private useHierarchySelectedFields(fields: FieldsValue) {
    Object.entries(fields).forEach(([field, value]) => {
      // If this field is a parent smart shape, see if the selected values for
      // that shape should be set on the child as well
      if (value.isSmartShape) {
        const parentShape = this.findRecord(value.id);
        const parentHierarchy = this.getHierarchy(parentShape!);
        const parentFields = this.fields[fieldAsRecordType(field)];

        // // Inherit parent selection, if there is no selection already
        Object.entries(parentFields).forEach(([field, value]) => {
          fields[field] ||= value;
        });
        Object.entries(parentHierarchy).forEach(([field, value]) => {
          fields[field] ||= value;
        });
      }
    });
  }

  // Wrapper to set the fields for a record from the user selected options
  private useSelectedFields(shape: Shape<ShapeSmartRecord>, fields) {
    const selectedFields = this.fields[shape.recordType!];
    Object.entries(selectedFields).forEach(([field, value]) => {
      fields[field] ||= value;
    });
  }

  private isParentForChild(
    parentType: SmartRecordCreatableType,
    childType: SmartRecordCreatableType
  ): boolean {
    let parentTypes: SmartRecordCreatableType[] = [];
    switch (childType) {
      case 'requirements':
        parentTypes = ['features'];
        break;
      case 'features':
        parentTypes = ['epics', 'releases'];
        break;
      case 'epics':
        parentTypes = ['releases'];
        break;
      case 'releases':
      case 'initiatives':
      case 'goals':
      case 'ideas':
      default:
        parentTypes = [];
    }
    return parentTypes.includes(parentType);
  }

  private isBelongsToForChild(
    parentType: string,
    childType: SmartRecordCreatableType
  ): boolean {
    let parentTypes: string[] = [];
    switch (childType) {
      case 'features':
      case 'epics':
      case 'releases':
        parentTypes = ['initiatives', 'goals'];
        break;
      case 'initiatives':
        parentTypes = ['goals'];
        break;
      default:
        parentTypes = [];
    }
    return parentTypes.includes(parentType);
  }

  isValidRelation(
    parentType: SmartRecordCreatableType,
    childType: SmartRecordCreatableType
  ) {
    return (
      this.isParentForChild(parentType, childType) ||
      this.isBelongsToForChild(parentType, childType)
    );
  }

  // Checks if any record for a given type is selected
  private get isRecordSelected() {
    return this.availableRecordTypes.some(recordType =>
      this.recordSelectedForType(recordType)
    );
  }

  private constructHierarchy(
    shape: Shape<ShapeSmartRecord>,
    fields: FieldsValue
  ) {
    // Prioritize nested shapes
    this.constructNestedHierarchy(shape, fields);

    if (this.controller.useConnectorHierarchies) {
      // Then, fill out hierarchy based on connected smart shapes
      const branches = this.collectBranches(shape, fields);
      this.selectBestBranches(branches, fields);
    }
  }

  private constructNestedHierarchy(
    shape: Shape<ShapeSmartRecord>,
    fields: FieldsValue
  ) {
    if (shape.container) {
      const parentShape = this.findRecord(shape.container);
      if (
        parentShape &&
        this.isValidRelation(
          parentShape.recordType as SmartRecordCreatableType,
          shape.recordType as SmartRecordCreatableType
        ) &&
        this.recordSelected(parentShape)
      ) {
        this.setParentAsField(parentShape, fields);
        this.constructNestedHierarchy(parentShape, fields);
      }
    }
  }

  // To keep the hierarchy code simple, we check valid parents for each node,
  // but not the child nodes. This function cleans up any invalid parents for
  // a given record that might get set, e.g., if we have epic -> feature -> requirement,
  // the requirement will have the epic in the hierarchy, which is not valid
  private pruneFields(shape: Shape<ShapeSmartRecord>, fields: FieldsValue) {
    Object.entries(fields).forEach(([field, value]) => {
      const parentType = fieldAsRecordType(field);
      if (
        value?.isSmartShape &&
        !this.isValidRelation(
          parentType,
          shape.recordType as SmartRecordCreatableType
        )
      ) {
        delete fields[field];
      }
    });
  }

  private setParentAsField(
    parentShape: Shape<ShapeSmartRecord>,
    fields: FieldsValue
  ) {
    const field = recordTypeAsField(
      parentShape.recordType as SmartRecordCreatableType
    );
    fields[field] = {
      id: parentShape.id,
      name: parentShape.plainText(),
      isSmartShape: true,
    };
  }

  // Finds all valid paths in the hierarchy for this smart shape. Paths will
  // exclude this smart shape. This will stop looking down a path if a record
  // of a given type is already accounted for (e.g., if the root is nested in
  // an epic, this alg will stop looking down paths once they see an epic in
  // that path). The idea is that we should only use the ancestors in a path if
  // we've used all the nodes up to that point.
  private collectBranches(root: Shape<ShapeSmartRecord>, fields: FieldsValue) {
    const branches = {};

    const dfs = (node, path) => {
      if (!node || fields[node.recordType] || !this.recordSelected(node))
        return;

      path.push(node);

      // Get connected objects, excluding the root and any visited nodes to
      // prevent backtracking. Also exclude invalid parent types
      const connectedObjects = getConnectorAttachmentObjects(node)
        .filter(obj => obj.id !== root.id)
        .filter(obj => !path.some(pathObj => pathObj.id === obj.id))
        .filter(obj =>
          this.isValidRelation(
            obj.recordType as SmartRecordCreatableType,
            node.recordType
          )
        );

      if (connectedObjects.length === 0) {
        // Leaf node reached, record the branch
        const recordType = path[0].recordType;
        branches[recordType] ||= [];
        branches[recordType].push([...path]);
      } else {
        for (const obj of connectedObjects) {
          if (
            this.isValidRelation(
              obj.recordType as SmartRecordCreatableType,
              node.recordType
            )
          ) {
            dfs(obj, path);
          }
        }
      }

      path.pop(); // Backtrack
    };

    // Start DFS from the root's connected objects
    const connectedObjects = getConnectorAttachmentObjects(root).filter(
      obj => obj.id !== root.id
    );
    for (const obj of connectedObjects) {
      if (
        this.isValidRelation(
          obj.recordType as SmartRecordCreatableType,
          root.recordType as SmartRecordCreatableType
        )
      ) {
        dfs(obj, []);
      }
    }

    return branches;
  }

  // The "best" branch has two conditions:
  // - Anything directly connected (i.e., 1 step away)
  // - In the event of a tie, we then use the longest path (in order to get the
  //   most ancestors)
  private selectBestBranches(branches, fields: FieldsValue) {
    // Sorting in create order is important, as it guarantees that the closest
    // parent has priority
    Object.keys(branches)
      .sort((a, b) => CREATE_ORDER.indexOf(a) - CREATE_ORDER.indexOf(b))
      .forEach(type => {
        const paths = branches[type];
        let bestPath: Shape<ShapeSmartRecord>[] = [];
        for (const path of paths) {
          if (path.length > bestPath.length) {
            bestPath = path;
          }
        }

        for (const object of bestPath) {
          // Early out if this field is already set. We don't want to set the rest
          // of the path if one of the intermediate steps doesn't get set
          if (
            fields[
              recordTypeAsField(object.recordType as SmartRecordCreatableType)
            ]
          ) {
            break;
          }

          this.setParentAsField(object, fields);
        }
      });
  }

  // Checks if all the required fields for a given shape have been filled out
  private requiredFieldsSatisfied(shape, fields) {
    const requiredFields = this.requiredFieldsForRecordType(shape.recordType);
    return Object.entries(requiredFields).every(([field, required]) => {
      if (!required) return true;

      return Boolean(fields[field]);
    });
  }
}

export default SmartRecordDataManager;
